/**
 * DRAFT-TRUTH-1 B (ruling TR-2026-10-22-9) — one dialog, two entry points:
 *   - mode "start":  Start project — pick the real start, preview, Confirm
 *                    (POST work/projects/:id/start { startDate });
 *   - mode "replan": Re-plan start date on a Draft (gated or not) — same
 *                    preview, no state change (POST …/start-date { startDate }).
 *
 * The preview is the SERVER's (POST …/start-date { dryRun: true }); opening
 * the dialog with no date returns "today in the organization's timezone", so
 * the default is never the browser's day. Nothing is updated optimistically:
 * Confirm waits for the server, then the page reloads its data.
 */
import { useCallback, useEffect, useState } from 'react';

import { previewLines, type StartDatePlan } from './startDatePreview';

import { request } from '@/lib/api';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function errorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
  return getApiErrorMessage(data) || fallback;
}

export function StartDateDialog({
  projectId,
  mode,
  onCancel,
  onDone,
}: {
  projectId: string;
  mode: 'start' | 'replan';
  onCancel: () => void;
  onDone: () => Promise<void> | void;
}) {
  const [date, setDate] = useState('');
  const [plan, setPlan] = useState<StartDatePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useCallback(
    async (startDate?: string) => {
      setLoading(true);
      setError(null);
      try {
        // `request` unwraps the { data, meta } envelope (raw `api` would
        // hand back { __zephixInner, __zephixMeta } because meta is present).
        const next = await request.post<StartDatePlan>(
          `/work/projects/${projectId}/start-date`,
          { ...(startDate ? { startDate } : {}), dryRun: true },
        );
        setPlan(next);
        if (!startDate) setDate(next.newAnchor ?? '');
      } catch (err: unknown) {
        setPlan(null);
        setError(errorText(err, 'Could not preview the new dates.'));
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  useEffect(() => {
    void preview();
  }, [preview]);

  const onDateChange = (value: string) => {
    setDate(value);
    if (DATE_ONLY.test(value)) void preview(value);
    else setPlan(null);
  };

  // TR-2026-10-22-20: with work under way nothing moves — Start changes state
  // only (no date needed); Re-plan has nothing to do and is not offered.
  const underWay = plan?.workUnderWay === true;
  const canConfirm =
    !loading &&
    !submitting &&
    plan !== null &&
    (underWay ? mode === 'start' : DATE_ONLY.test(date));

  const confirm = async () => {
    if (!canConfirm || !plan) return;
    setSubmitting(true);
    setError(null);
    try {
      const path =
        mode === 'start'
          ? `/work/projects/${projectId}/start`
          : `/work/projects/${projectId}/start-date`;
      await request.post(path, underWay ? {} : { startDate: date });
      await onDone();
    } catch (err: unknown) {
      setError(errorText(err, mode === 'start' ? 'Could not start the project.' : 'Could not re-plan the start date.'));
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'start' ? 'Start project' : 'Re-plan start date';
  const headingId = `start-date-dialog-${mode}`;
  return (
    <section
      className="w-full rounded border p-3"
      role="dialog"
      aria-labelledby={headingId}
      data-testid="start-date-dialog"
    >
      <h3 id={headingId} className="text-sm font-semibold">
        {title}
      </h3>
      <label className="mt-2 block text-sm" htmlFor={`${headingId}-date`}>
        {mode === 'start' ? 'Real start date' : 'Planned start date'}
        <input
          id={`${headingId}-date`}
          type="date"
          className="ml-2 rounded border px-2 py-1"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          disabled={submitting || underWay}
        />
      </label>
      <div className="mt-2 text-sm" aria-live="polite" data-testid="start-date-preview">
        {loading ? (
          <p>Checking which dates move…</p>
        ) : plan ? (
          previewLines(plan).map((line) => <p key={line}>{line}</p>)
        ) : null}
      </div>
      {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className="rounded border px-2 py-1 text-sm"
          onClick={() => void confirm()}
          disabled={!canConfirm}
          title={
            underWay && mode === 'replan'
              ? 'Work is under way; dates stay as they are.'
              : !plan
                ? 'Pick a date to see which dates move first.'
                : undefined
          }
        >
          {submitting ? 'Saving…' : mode === 'start' ? 'Confirm start' : 'Confirm new start date'}
        </button>
        <button type="button" className="text-sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
    </section>
  );
}
